import { cn } from 'cn';
import { useTranslations } from 'use-intl';

import { Button } from '@/shared-ui';

import type { MatrixRow } from './survey-ai-forms-ai-section-matrix-question.hooks';

interface SortedOption {
  label: string;
  value: string;
}

interface Props {
  row: MatrixRow;
  options: SortedOption[];
  naEnabled: boolean;
  onSelect: (subQuestionId: string, value: string) => void;
  onToggleNa: (subQuestionId: string) => void;
  isMobile: boolean;
}

export default function SurveyAiFormsAiSectionMatrixQuestionRow({
  row,
  options,
  naEnabled,
  onSelect,
  onToggleNa,
  isMobile,
}: Props) {
  const t = useTranslations('AIForms');

  return (
    <div
      data-test='matrix-row'
      data-is-na={row.isNa}
      data-is-mobile={isMobile}
      className='flex w-full gap-4 data-[is-mobile=false]:items-center data-[is-mobile=false]:gap-8 data-[is-mobile=true]:flex-col data-[is-mobile=true]:items-start'
    >
      <p
        data-test='matrix-row-text'
        data-is-mobile={isMobile}
        className='shrink-0 [font-family:var(--brand-font)] text-sm/5 font-bold text-(--brand-secondary) data-[is-mobile=false]:w-80 data-[is-mobile=true]:w-full'
      >
        {row.text}
      </p>

      <div className='flex w-full flex-1 items-center gap-4'>
        <div
          data-is-na={row.isNa}
          className='flex flex-1 items-center gap-2 data-[is-na=true]:pointer-events-none data-[is-na=true]:opacity-30'
        >
          {options.map((opt) => {
            const selected = row.value === opt.value;
            return (
              <Button
                key={opt.value}
                type='button'
                variant='ghost'
                size='sm'
                data-test='matrix-rating-pill'
                data-selected={selected}
                aria-pressed={selected}
                aria-label={`${t('matrix.rateAriaPrefix')} ${row.text} ${opt.label}`}
                onClick={() => onSelect(row.id, opt.value)}
                className={cn(
                  'h-10 min-w-0 flex-1 rounded-full border border-solid border-[#e3e3f2] bg-white px-2 [font-family:var(--brand-font)] text-sm/5 font-semibold text-black transition-none hover:enabled:bg-white hover:enabled:text-black',
                  'data-[selected=true]:border-(--brand-primary) data-[selected=true]:bg-(--brand-primary) data-[selected=true]:text-(--brand-primary-contrast) data-[selected=true]:shadow-[0px_15px_12.5px_rgba(39,39,83,0.15),0px_5px_5px_rgba(39,39,83,0.05)] data-[selected=true]:hover:enabled:bg-(--brand-primary) data-[selected=true]:hover:enabled:text-(--brand-primary-contrast)'
                )}
              >
                {opt.value}
              </Button>
            );
          })}
        </div>

        {naEnabled && (
          <Button
            type='button'
            variant='ghost'
            size='sm'
            role='switch'
            aria-checked={row.isNa ? 'true' : 'false'}
            aria-label={t('matrix.markNaAria', { row: row.text })}
            data-test='matrix-na-toggle'
            data-checked={row.isNa}
            onClick={() => onToggleNa(row.id)}
            className='h-5.5 w-8 shrink-0 justify-start rounded-full bg-[#e3e3f2] p-0.5 backdrop-blur-sm transition-none hover:enabled:bg-[#e3e3f2] data-[checked=true]:justify-end data-[checked=true]:bg-(--brand-primary) data-[checked=true]:shadow-[0px_4px_12px_rgba(52,52,111,0.24)] data-[checked=true]:hover:enabled:bg-(--brand-primary)'
          >
            <span className='block size-4.5 rounded-full bg-white' />
          </Button>
        )}
      </div>
    </div>
  );
}

export type { SortedOption };
